import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog as DialogPrimitive } from "radix-ui";
import { ArrowLeft, Compass, HelpCircle, Loader2, X } from "lucide-react";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { useLandscapeData } from "@/hooks/useLandscapeData";
import { prefetchExpandPapers } from "@/hooks/usePapers";
import { guideSignals } from "@/lib/guideSignals";
import {
  LandscapeCanvas,
  type LandscapeCanvasHandle,
} from "@/components/landscape/LandscapeCanvas";
import { LandscapeSidebar } from "@/components/landscape/LandscapeSidebar";
import { LandscapeOrientationCard } from "@/components/landscape/LandscapeOrientationCard";
import { ROLE_COLORS } from "@/lib/viz-colors";
import { computeFingerprint, isStale } from "@/lib/resultFingerprint";

// App-level (not per-concept) — orientation is the same on every concept's map.
const ORIENTED_KEY = "paperchase-landscape-oriented";

const ROLE_LABELS: Record<string, string> = {
  supports: "Supports",
  challenges: "Challenges",
  extends: "Extends",
  applies: "Applies",
  operationalises: "Operationalises",
  reviews: "Reviews",
};

interface Props {
  conceptId: string;
  conceptName: string;
  /**
   * If provided, render only papers whose IDs are in this set. Lets the
   * Analyze page's filters narrow the visible dots without invalidating
   * the cached UMAP coords (which are panoramic and shared across views).
   */
  paperIdScope?: string[];
  /**
   * Active-corpus paper IDs (trawled minus dismissed) — what the backend
   * landscape considers as candidates regardless of UI filters. This is
   * the set we snapshot into the staleness fingerprint, so dismiss /
   * restore / new-trawl flips STALE on the rail but a year-filter toggle
   * does not.
   */
  corpusPaperIds?: string[];
  /** Working set ID at fetch time. Bundled into the staleness fingerprint
   *  so toggling working set surfaces STALE on the rail. */
  wsId?: string | null;
}

export function LandscapeOverlay({
  conceptId,
  conceptName,
  paperIdScope,
  corpusPaperIds,
  wsId,
}: Props) {
  const isOpen = useLandscapeStore((s) => s.isOpen);
  const close = useLandscapeStore((s) => s.close);
  const hoveredId = useLandscapeStore((s) => s.hoveredId);
  const selectedId = useLandscapeStore((s) => s.selectedId);
  const setHoveredId = useLandscapeStore((s) => s.setHoveredId);
  const setSelectedId = useLandscapeStore((s) => s.setSelectedId);
  const visibleRoles = useLandscapeStore((s) => s.visibleRoles);
  const toggleRole = useLandscapeStore((s) => s.toggleRole);
  const colorBy = useLandscapeStore((s) => s.colorBy);
  const openWalkthrough = useGuidesStore((s) => s.openWalkthrough);

  // "What am I looking at?" orientation card — shown once on first-ever open,
  // re-openable via the header "?" button.
  const [showOrientation, setShowOrientation] = useState(false);
  const dismissOrientation = useCallback(() => {
    setShowOrientation(false);
    try {
      localStorage.setItem(ORIENTED_KEY, "1");
    } catch {
      // ignore storage failures — worst case the card shows again
    }
  }, []);
  const handleLearnMore = useCallback(() => {
    dismissOrientation();
    openWalkthrough("leveraging-the-analyze-page", "landscape");
  }, [dismissOrientation, openWalkthrough]);

  const cachedData = useLandscapeStore((s) => s.cachedData);
  const cachedConceptId = useLandscapeStore((s) => s.cachedConceptId);
  const cachedFingerprint = useLandscapeStore((s) => s.cachedFingerprint);
  const setCachedData = useLandscapeStore((s) => s.setCachedData);
  const animatedConceptId = useLandscapeStore((s) => s.animatedConceptId);
  const markAnimated = useLandscapeStore((s) => s.markAnimated);

  const canvasRef = useRef<LandscapeCanvasHandle>(null);
  const { mutate, data: freshData, isPending, error } = useLandscapeData(conceptId);

  // Use cached data if available for this concept, otherwise use fresh mutation data
  const data = (cachedConceptId === conceptId ? cachedData : null) ?? freshData ?? null;

  // Stale when the cached fingerprint diverges from the current
  // active-corpus set. threshold=0 so a single dismiss / restore /
  // new trawl flips the rail STALE — the landscape's dot count
  // changes visibly with even one paper added or removed, so we
  // don't want to wait for the default 10% Jaccard threshold.
  const stale = useMemo(() => {
    if (!cachedFingerprint || cachedConceptId !== conceptId) return false;
    if (!corpusPaperIds) return false;
    return isStale(
      cachedFingerprint,
      { paperIds: corpusPaperIds, wsId: wsId ?? null },
      0,
    );
  }, [cachedFingerprint, cachedConceptId, conceptId, corpusPaperIds, wsId]);

  // Apply Analyze's filter scope by hiding out-of-scope dots. UMAP coords are
  // panoramic (computed for the whole corpus); we just narrow what renders.
  const scopedData = useMemo(() => {
    if (!data || !paperIdScope) return data;
    const inScope = new Set(paperIdScope);
    return { ...data, papers: data.papers.filter((p) => inScope.has(p.paper_id)) };
  }, [data, paperIdScope]);

  // Cache fresh data when it arrives, snapshotting the active-corpus
  // set the landscape was computed for. Falls back to placed-paper IDs
  // from the response when corpusPaperIds isn't supplied (e.g., direct
  // /map route consumers that don't track the Analyze active set).
  useEffect(() => {
    if (freshData) {
      const fingerprint = computeFingerprint({
        paperIds: corpusPaperIds ?? freshData.papers.map((p) => p.paper_id),
        wsId: wsId ?? null,
        scoredCount: 0, // landscape doesn't gate on scored-count
      });
      setCachedData(conceptId, freshData, fingerprint);
    }
  }, [freshData, conceptId, setCachedData, corpusPaperIds, wsId]);

  // Fetch on open OR when the cached map is stale relative to current scope.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isOpen) {
      guideSignals.mark(conceptId, "hasViewedLandscape");
      if (!data || stale) mutate();
      // Warm Expand's papers cache while the user reads the landscape —
      // "Open in Expand" navigates to a page that needs full-shape trawled +
      // foundational, neither of which Analyze caches (Analyze uses
      // shape='analytics' for trawled, doesn't fetch foundational at all).
      // Without this prefetch, Landscape→Expand has a 3-5s cold-cache fetch.
      prefetchExpandPapers(queryClient, conceptId);
    }
  }, [isOpen, mutate, data, stale, conceptId, queryClient]);

  // Mark this concept as animated after the reveal completes, so a return
  // visit (Expand → Back to Landscape) skips the fade-in. The 2.5s delay
  // matches LandscapeCanvas's ANIM_DURATION — flipping the flag earlier
  // would re-run the canvas papers-effect with skipReveal=true mid-flight
  // and cut the animation off the moment it started.
  // setCachedData resets animatedConceptId to null whenever fresh data
  // lands (initial fetch or stale-driven recompute), so a real refresh
  // re-animates once. Cleanup cancels the pending mark if the user closes
  // before 2.5s — they get a fresh animation on the next open.
  useEffect(() => {
    if (!isOpen || !data || animatedConceptId === conceptId) return;
    const t = setTimeout(() => markAnimated(conceptId), 2500);
    return () => clearTimeout(t);
  }, [isOpen, data, conceptId, animatedConceptId, markAnimated]);

  // Escape handling: deselect first, then close
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedId) {
          e.stopPropagation();
          setSelectedId(null);
        }
        // Let Radix handle close if nothing selected
      }
    },
    [selectedId, setSelectedId],
  );

  const handleZoomToCamp = useCallback((campId: string) => {
    canvasRef.current?.zoomToCamp(campId);
  }, []);

  const handleZoomToRegion = useCallback((x: number, y: number) => {
    canvasRef.current?.zoomToRegion(x, y);
  }, []);

  // Show the orientation card the first time the user ever opens the Landscape.
  useEffect(() => {
    if (!isOpen) return;
    let oriented = false;
    try {
      oriented = localStorage.getItem(ORIENTED_KEY) === "1";
    } catch {
      // treat storage failure as "not oriented"
    }
    if (!oriented) setShowOrientation(true);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-gray-900/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl focus:outline-none dark:bg-gray-900"
          onKeyDown={handleKeyDown}
        >
          <DialogPrimitive.Title className="sr-only">
            Literature Landscape
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Interactive scatter plot of your research corpus
          </DialogPrimitive.Description>

          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700">
            <button
              type="button"
              onClick={close}
              className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800"
            >
              <ArrowLeft className="size-3.5" />
              Back to Analyze
            </button>

            {/* Role filter pills */}
            <div className="flex items-center gap-1.5">
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleRole(key)}
                  className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                    visibleRoles.has(key)
                      ? "border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                      : "border-transparent bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
                  }`}
                >
                  <span
                    className="inline-block size-1.5 rounded-full"
                    style={{
                      background: visibleRoles.has(key)
                        ? ROLE_COLORS[key]
                        : "#ccc",
                    }}
                  />
                  {label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="What am I looking at?"
                title="What am I looking at?"
                onClick={() => setShowOrientation(true)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HelpCircle className="size-5" />
              </button>
              <button
                type="button"
                aria-label="Close landscape"
                onClick={close}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="size-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative flex flex-1 min-h-0">
            {isPending ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="size-6 animate-spin text-purple-500" />
                  <span className="text-sm text-gray-500">
                    Computing landscape...
                  </span>
                </div>
              </div>
            ) : error && (error as Error).message?.includes("409") ? (
              // 409 is a precondition, not a failure: the landscape positions
              // and colors papers from the Field Debate map, so it can't be
              // computed until that's been run. Render a calm empty-state (no
              // red, no "Try again" — retrying just 409s again) that points the
              // user back to Build/Analyze where Field Debate is launched.
              <div className="flex flex-1 items-center justify-center p-8">
                <div className="flex max-w-sm flex-col items-center gap-3 text-center">
                  <Compass className="size-8 text-gray-300" />
                  <h3 className="text-sm font-medium text-gray-700">
                    Run Field Debate to unlock the landscape
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-500">
                    The landscape positions and colors your papers using the
                    Field Debate map. Generate it from the Build or Analyze page,
                    then reopen the landscape.
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-1 rounded bg-purple-100 px-3 py-1 text-xs text-purple-700 hover:bg-purple-200"
                  >
                    Got it
                  </button>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center">
                  <span className="text-sm text-red-600">
                    {(error as Error).message?.includes("400")
                      ? "Need at least 15 papers with embeddings."
                      : "Couldn't compute landscape."}
                  </span>
                  <button
                    type="button"
                    onClick={() => mutate()}
                    className="rounded bg-purple-100 px-3 py-1 text-xs text-purple-700 hover:bg-purple-200"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : scopedData ? (
              <>
                {showOrientation && (
                  <LandscapeOrientationCard
                    onClose={dismissOrientation}
                    onLearnMore={handleLearnMore}
                  />
                )}
                <LandscapeCanvas
                  ref={canvasRef}
                  papers={scopedData.papers}
                  camps={scopedData.camps}
                  tensions={scopedData.tensions}
                  thesis={scopedData.thesis}
                  visibleRoles={visibleRoles}
                  colorBy={colorBy}
                  hoveredId={hoveredId}
                  selectedId={selectedId}
                  onHover={setHoveredId}
                  onSelect={setSelectedId}
                  skipReveal={animatedConceptId === conceptId}
                />
                <LandscapeSidebar
                  conceptId={conceptId}
                  conceptName={conceptName}
                  papers={scopedData.papers}
                  camps={scopedData.camps}
                  thesis={scopedData.thesis}
                  onZoomToCamp={handleZoomToCamp}
                  onZoomToRegion={handleZoomToRegion}
                  candidateTotal={data?.candidate_total}
                />
              </>
            ) : null}
          </div>

          {/* Footer legend */}
          {scopedData && (
            <div className="flex items-center gap-4 border-t border-gray-200 bg-gray-50 px-4 py-1.5 dark:border-gray-700 dark:bg-gray-800">
              <span className="text-[10px] text-gray-500">
                Closer = more similar
              </span>
              <span className="text-[10px] text-gray-500">
                Color:{" "}
                <span className="text-gray-700 dark:text-gray-300">
                  {colorBy === "cluster"
                    ? "topic clusters"
                    : "argumentation roles"}
                </span>
              </span>
              <span className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
              {[
                { label: "Read", opacity: 0.93 },
                { label: "Skimmed", opacity: 0.55 },
                { label: "Unread", opacity: 0.36 },
              ].map((s) => (
                <span
                  key={s.label}
                  className="flex items-center gap-1.5 text-[10px] text-gray-500"
                >
                  <span
                    className="inline-block size-1.5 rounded-full"
                    style={{ background: `rgba(128,128,128,${s.opacity})` }}
                  />
                  {s.label}
                </span>
              ))}
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="inline-block size-1.5 rounded-full border border-gray-400" />
                Bridge
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="relative inline-block size-2.5 rounded-full bg-gray-400">
                  <span className="absolute inset-0 m-auto size-1 rounded-full bg-white" />
                </span>
                Evidence
              </span>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
